import { z } from "zod";

/**
 * Sözleşme dosyası KABUL politikası (CLOS-109 `[070]`).
 *
 * ### ⚠ Depolamanın politikasından AYRI ve daha DAR
 *
 * `[005]` CLOS-212 ürün geneli bir dosya politikası taşıyor (PNG ve JPEG dahil). Sözleşme
 * içe aktarma o kümenin ALT KÜMESİ: bir ekran görüntüsü depolanabilir bir dosyadır ama
 * sözleşme belgesi değildir. Tek politika kullansaydık kullanıcı bir PNG'yi sözleşme
 * olarak yükleyebilir ve ürün onu bir belge gibi işlemeye çalışırdı.
 *
 * ### Değerler KONFİGÜRASYONDAN (kartın açık kararı)
 *
 * *"Desteklenen sözleşme içe aktarma biçimleri, boyut sınırları ve doğrulama kuralları
 * konfigüre edilecektir"* ve *"parametreler hard-code edilmez."* Değerler
 * `config/contractFile.json`'da; burada yalnız o dosyanın ŞEKLİ.
 */
export const contractFilePolicySchema = z.object({
  allowedContentTypes: z.array(z.string().min(1)).min(1),
  maxBytes: z.number().int().positive(),
  /** Parola korumalı belge kabul edilsin mi. */
  rejectEncrypted: z.boolean(),
});
export type ContractFilePolicy = z.infer<typeof contractFilePolicySchema>;

/**
 * Reddetme SEBEPLERİ — kapalı küme.
 *
 * ⚠ Kart: *"hata mesajı kullanıcıya DÜZELTİLEBİLİR nedeni açıklar; iç güvenlik ayrıntısı,
 * tenant bilgisi veya zararlı içerik imzası SIZDIRMAZ."* Bu yüzden sebepler kaba: kullanıcı
 * "ne yapmalıyım?" sorusunun cevabını alıyor, saldırgan ise doğrulayıcının iç mantığını
 * öğrenmiyor.
 */
export const CONTRACT_FILE_REJECTIONS = [
  "unsupported_type",
  "too_large",
  "empty",
  /** Beyan edilen tür ile dosyanın GERÇEK türü uyuşmuyor. */
  "type_mismatch",
  "encrypted",
  "corrupt",
  /** Güvenlik taraması bitmedi ya da dosyayı reddetti. */
  "not_scanned",
  /**
   * DOCX PAKET REDLERİ (CLOS-1230 — DOCX Kanonu §21).
   *
   * ⚠ `corrupt`'tan AYRI tutuldu. "Belge bozuk" ile "belge makro taşıyor" kullanıcı için
   * iki farklı durum: birincisinde dosyayı yeniden kaydetmek çözer, ikincisinde çözmez.
   * Tek sebebe indirseydik kullanıcıya yanlış çözümü söylerdik.
   */
  "docx_malformed",
  "docx_macro",
  "docx_zip_bomb",
] as const;
export type ContractFileRejection = (typeof CONTRACT_FILE_REJECTIONS)[number];

export const CONTRACT_FILE_REJECTION_NAMES: Record<ContractFileRejection, string> = {
  unsupported_type: "Bu dosya türü sözleşme olarak yüklenemiyor. PDF veya Word belgesi yükleyin.",
  too_large: "Dosya boyutu sınırı aşıyor.",
  empty: "Dosya boş görünüyor.",
  /**
   * ⚠ "Uzantıyı değiştirmişsiniz" DEMİYOR: kullanıcı çoğu zaman bunu bilerek yapmadı
   * (kaydeden program yanlış uzantı vermiş olabilir) ve suçlayıcı bir cümle yardımcı olmaz.
   */
  type_mismatch: "Dosya içeriği belirtilen türle uyuşmuyor. Belgeyi yeniden kaydedip deneyin.",
  encrypted: "Parola korumalı belgeler yüklenemiyor. Korumayı kaldırıp yeniden deneyin.",
  corrupt: "Belge okunamadı; dosya bozuk olabilir. Yeniden kaydedip deneyin.",
  not_scanned: "Dosya güvenlik taramasından geçmedi; şu an kullanılamaz.",
  docx_malformed: "Word belgesi okunamadı; paket eksik ya da bozuk görünüyor.",
  /**
   * ⚠ "Makro" kelimesi kullanıcı diline yakın ve ne yapacağını söylüyor. Teknik sebebi
   * (`vbaProject.bin` parçası bulundu) yazmak, atlatma yolunu anlatmak olurdu.
   */
  docx_macro: "Makro içeren Word belgeleri yüklenemiyor. Belgeyi .docx olarak kaydedin.",
  docx_zip_bomb: "Belge beklenenden çok büyük açılıyor; güvenlik gereği kabul edilmedi.",
};

/**
 * İstemci tarafı ÖN kontrol — bir güvenlik kararı DEĞİL.
 *
 * Kart: *"istemci tarafı kontrolleri kullanıcıya erken geri bildirim sağlar; GÜVENLİK
 * KARARI DEĞİLDİR."* Aynı sorular sunucuda yeniden soruluyor ve orada dosyanın gerçek
 * içeriğine de bakılıyor — burada yalnız tarayıcının bildiği iki şey var: tür ve boyut.
 */
export function precheckContractFile(
  policy: ContractFilePolicy,
  file: { contentType: string; sizeBytes: number },
): ContractFileRejection | null {
  if (!policy.allowedContentTypes.includes(file.contentType)) return "unsupported_type";
  if (file.sizeBytes <= 0) return "empty";
  if (file.sizeBytes > policy.maxBytes) return "too_large";
  return null;
}
